/**
 * TNK-124 phase G — resolve N storefront cards by id without forcing
 * the layout to pre-fetch the entire inventory.
 *
 * Order of resolution per id:
 *   1. The warmed first-page cache from `useShopInventory()` — cheap,
 *      already in memory for the common "click into a card from the
 *      inventory grid" flow.
 *   2. On miss, a single-card lookup against `/api/store/cards/:id` —
 *      handles deep links + cart items beyond the warmed page.
 *   3. Still-null after the fetch settles → the id is dropped from
 *      `cards` and surfaced via `missing.size` so callers can render
 *      a "no longer available" hint (the cart's existing prune).
 *
 * Re-runs on id-set change, dedupes parallel fetches across
 * components mounted at the same time via a small module-level
 * cache. The cache is per-page-load only — a full refresh is the
 * cheapest way to bust it and the storefront data mutates slowly.
 */
import { useEffect, useMemo, useState } from "react";

import {
  fetchStorefrontCard,
  type StorefrontCard,
} from "@/features/storefront";
import { useShopInventory } from "@/routes/shop";

interface LookupResult {
  /** Both the inventory prewarm and any active id-fetches have
   *  settled. `cards` is final at this point. */
  phase: "loading" | "ready" | "error";
  /** Cards in the same order as the input `ids`. Missing ids are
   *  filtered out so the caller can render the matching cards
   *  directly without an extra null check. */
  cards: StorefrontCard[];
  byId: Map<string, StorefrontCard>;
  /** Ids the lookup couldn't resolve (active sold-out, archived,
   *  or returned 404 on single-card fetch). */
  missing: Set<string>;
}

// Module-level cache shared across hook instances. Avoids re-fetching
// the same card twice when, say, the cart drawer and the cart page
// both mount with overlapping ids in the same nav.
const cardCache = new Map<string, StorefrontCard | null>();
const inflight = new Map<string, Promise<StorefrontCard | null>>();

function fetchOnce(id: string): Promise<StorefrontCard | null> {
  if (cardCache.has(id)) return Promise.resolve(cardCache.get(id) ?? null);
  const existing = inflight.get(id);
  if (existing) return existing;
  const p = fetchStorefrontCard(id)
    .catch(() => null)
    .then((card) => {
      cardCache.set(id, card);
      inflight.delete(id);
      return card;
    });
  inflight.set(id, p);
  return p;
}

export function useStorefrontCardLookup(ids: readonly string[]): LookupResult {
  const inventory = useShopInventory();
  // Snapshot of fetched-extra cards by id. Keys are only ids we
  // tried to look up via the single-card endpoint.
  const [extras, setExtras] = useState<Map<string, StorefrontCard | null>>(
    () => new Map(),
  );

  // Build a lookup map from the warmed inventory page once per
  // inventory state change.
  const inventoryById = useMemo(() => {
    if (inventory.phase !== "ready") return new Map<string, StorefrontCard>();
    const m = new Map<string, StorefrontCard>();
    for (const c of inventory.cards) m.set(c.id, c);
    return m;
  }, [inventory]);

  // Stable key for the id set so the fetch effect only re-runs when
  // the actual ids change (not just array identity).
  const idKey = useMemo(() => [...ids].sort().join("|"), [ids]);

  useEffect(() => {
    if (inventory.phase !== "ready") return;
    const todo = ids.filter(
      (id) => !inventoryById.has(id) && !extras.has(id) && !cardCache.has(id),
    );
    if (todo.length === 0) {
      // Seed extras from the module cache so the next render
      // synchronously resolves ids that were already fetched by
      // another component.
      const cached = ids.filter(
        (id) => !inventoryById.has(id) && cardCache.has(id),
      );
      if (cached.length > 0) {
        setExtras((prev) => {
          const next = new Map(prev);
          for (const id of cached) {
            if (!next.has(id)) next.set(id, cardCache.get(id) ?? null);
          }
          return next;
        });
      }
      return;
    }
    let cancelled = false;
    void (async () => {
      const fetched = await Promise.all(todo.map((id) => fetchOnce(id)));
      if (cancelled) return;
      setExtras((prev) => {
        const next = new Map(prev);
        todo.forEach((id, i) => next.set(id, fetched[i]));
        return next;
      });
    })();
    return () => {
      cancelled = true;
    };
    // idKey covers the ids axis; inventoryById / extras are intentionally
    // omitted to avoid a re-fire after our own setExtras.
  }, [idKey, inventory.phase, inventoryById, ids, extras]);

  const byId = useMemo(() => {
    const m = new Map<string, StorefrontCard>();
    for (const id of ids) {
      const fromInv = inventoryById.get(id);
      if (fromInv) {
        m.set(id, fromInv);
        continue;
      }
      const fromExtras = extras.get(id);
      if (fromExtras) m.set(id, fromExtras);
    }
    return m;
  }, [ids, inventoryById, extras]);

  const cards = useMemo(() => {
    const out: StorefrontCard[] = [];
    for (const id of ids) {
      const c = byId.get(id);
      if (c) out.push(c);
    }
    return out;
  }, [ids, byId]);

  const missing = useMemo(() => {
    const out = new Set<string>();
    for (const id of ids) {
      if (byId.has(id)) continue;
      if (extras.has(id) && extras.get(id) === null) out.add(id);
    }
    return out;
  }, [ids, byId, extras]);

  // Loading while the inventory prewarm hasn't landed or there's an
  // outstanding fetch for an id we haven't seen yet.
  const stillFetching = ids.some(
    (id) =>
      !inventoryById.has(id) && !extras.has(id) && inventory.phase === "ready",
  );
  const phase: LookupResult["phase"] =
    inventory.phase === "error"
      ? "error"
      : inventory.phase === "loading" || stillFetching
        ? "loading"
        : "ready";

  return { phase, cards, byId, missing };
}
